import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useApiQuery } from '../../../hooks/useApiQuery';
import { useApiMutation } from '../../../hooks/useMutation';
import { US_STATES } from '../../../utils/Constant';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';

interface Election {
  id: number;
  googleElectionId: string;
  name: string;
  electionDay: string;
  ocdDivisionId: string;
  electionType: string;
  stateCode: string;
  electionHours: string | null;
  registrationDeadline: string;
  mailRequestDeadline: string;
  mailReturnDeadline: string;
  earlyVotingStartDate: string | null;
  earlyVotingEndDate: string | null;
  voterIdRequirement: string | null;
  felonyVotingRules: string | null;
  electionInfoUrl: string;
  electionRegistrationUrl: string;
  electionRegistrationConfirmationUrl: string | null;
  absenteeVotingInfoUrl: string | null;
  votingLocationFinderUrl: string | null;
  ballotInfoUrl: string | null;
  electionRulesUrl: string | null;
  officialStateWebsite: string;
  voterInfoEnriched: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ElectionData {
  elections: Election[];
}

interface ElectionResponse {
  success: boolean;
  message: string;
  data: ElectionData;
}

interface FormData {
  googleElectionId: string | null;
  name: string;
  electionDay: string;
  ocdDivisionId: string;
  electionType: string;
  stateCode: string;
  electionHours: string;
  registrationDeadline: string;
  mailRequestDeadline: string;
  mailReturnDeadline: string;
  earlyVotingStartDate: string;
  earlyVotingEndDate: string;
  voterIdRequirement: string;
  felonyVotingRules: string;
  electionInfoUrl: string;
  electionRegistrationUrl: string;
  electionRegistrationConfirmationUrl: string;
  absenteeVotingInfoUrl: string;
  votingLocationFinderUrl: string;
  ballotInfoUrl: string;
  electionRulesUrl: string;
  officialStateWebsite: string;
  voterInfoEnriched: boolean;
  electionResultsUrl: string;
}

const initialFormData: FormData = {
  googleElectionId: null,
  name: '',
  electionDay: '',
  ocdDivisionId: '',
  electionType: '',
  stateCode: '',
  electionHours: '',
  registrationDeadline: '',
  mailRequestDeadline: '',
  mailReturnDeadline: '',
  earlyVotingStartDate: '',
  earlyVotingEndDate: '',
  voterIdRequirement: '',
  felonyVotingRules: '',
  electionInfoUrl: '',
  electionRegistrationUrl: '',
  electionRegistrationConfirmationUrl: '',
  absenteeVotingInfoUrl: '',
  votingLocationFinderUrl: '',
  ballotInfoUrl: '',
  electionRulesUrl: '',
  officialStateWebsite: '',
  voterInfoEnriched: false,
  electionResultsUrl: ''
};

const ElectionForm = ({ 
  formData, 
  electionIndex, 
  isFormDisabled, 
  handleInputChange,
  isExpanded,
  onToggleExpand,
  showCollapseButton
}: { 
  formData: FormData; 
  electionIndex: number;
  isFormDisabled: boolean; 
  handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => void;
  isExpanded: boolean;
  onToggleExpand: () => void;
  showCollapseButton: boolean;
}) => {
  return (
    <div className="border border-gray-200 rounded-lg mb-6 overflow-hidden">
      <div 
        className={`flex items-center justify-between p-4 bg-gray-50 ${showCollapseButton ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors`}
        onClick={showCollapseButton ? onToggleExpand : undefined}
      >
        <h3 className="text-xl font-semibold text-gray-600">
          {formData.name} ({formData.electionType})
        </h3>
        {showCollapseButton && (
          <div className="flex items-center text-gray-600">
            <span className="text-sm mr-2">
              {isExpanded ? 'Click to collapse' : 'Click to expand'}
            </span>
            {isExpanded ? (
              <ChevronUpIcon className="h-5 w-5" />
            ) : (
              <ChevronDownIcon className="h-5 w-5" />
            )}
          </div>
        )}
      </div>

      {/* Form Content */}
      {isExpanded && (
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Day
              </label>
              <input
                type="date"
                name="electionDay"
                value={formData.electionDay}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OCD Division ID
              </label>
              <input
                type="text"
                name="ocdDivisionId"
                value={formData.ocdDivisionId}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Type
              </label>
              <input
                type="text"
                name="electionType"
                value={formData.electionType}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State Code
              </label>
              <input
                type="text"
                name="stateCode"
                value={formData.stateCode}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Hours
              </label>
              <input
                type="text"
                name="electionHours"
                value={formData.electionHours}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Deadline
              </label>
              <input
                type="date"
                name="registrationDeadline"
                value={formData.registrationDeadline}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mail Request Deadline
              </label>
              <input
                type="date"
                name="mailRequestDeadline"
                value={formData.mailRequestDeadline}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mail Return Deadline
              </label>
              <input
                type="date"
                name="mailReturnDeadline"
                value={formData.mailReturnDeadline}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Early Voting Start Date
              </label>
              <input
                type="date"
                name="earlyVotingStartDate"
                value={formData.earlyVotingStartDate}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Early Voting End Date
              </label>
              <input
                type="date"
                name="earlyVotingEndDate"
                value={formData.earlyVotingEndDate}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voter ID Requirement
              </label>
              <input
                type="text"
                name="voterIdRequirement"
                value={formData.voterIdRequirement}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Felony Voting Rules
              </label>
              <input
                type="text"
                name="felonyVotingRules"
                value={formData.felonyVotingRules}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Info URL
              </label>
              <input
                type="url"
                name="electionInfoUrl"
                value={formData.electionInfoUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Registration URL
              </label>
              <input
                type="url"
                name="electionRegistrationUrl"
                value={formData.electionRegistrationUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registration Confirmation URL
              </label>
              <input
                type="url"
                name="electionRegistrationConfirmationUrl"
                value={formData.electionRegistrationConfirmationUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Absentee Voting Info URL
              </label>
              <input
                type="url"
                name="absenteeVotingInfoUrl"
                value={formData.absenteeVotingInfoUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Voting Location Finder URL
              </label>
              <input
                type="url"
                name="votingLocationFinderUrl"
                value={formData.votingLocationFinderUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ballot Info URL
              </label>
              <input
                type="url"
                name="ballotInfoUrl"
                value={formData.ballotInfoUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Rules URL
              </label>
              <input
                type="url"
                name="electionRulesUrl"
                value={formData.electionRulesUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Official State Website
              </label>
              <input
                type="url"
                name="officialStateWebsite"
                value={formData.officialStateWebsite}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Election Results URL
              </label>
              <input
                type="url"
                name="electionResultsUrl"
                value={formData.electionResultsUrl}
                onChange={(e) => handleInputChange(e, electionIndex)}
                disabled={isFormDisabled}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              name="voterInfoEnriched"
              checked={formData.voterInfoEnriched}
              onChange={(e) => handleInputChange(e, electionIndex)}
              disabled={isFormDisabled}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded disabled:cursor-not-allowed"
            />
            <label htmlFor="voterInfoEnriched" className="ml-2 block text-sm text-gray-900">
              Voter Info Enriched
            </label>
          </div>
        </div>
      )}
    </div>
  );
};

export default function UpdateElection() {
  const [selectedState, setSelectedState] = useState<string>('');
  const [electionsData, setElectionsData] = useState<Election[]>([]);
  const [formsData, setFormsData] = useState<FormData[]>([]);
  const [expandedForms, setExpandedForms] = useState<Set<number>>(new Set());

  const { data: electionResponse, isLoading } = useApiQuery<ElectionResponse>({
    queryKey: ['elections', 'state', selectedState],
    request: {
      method: 'GET',
      url: `/elections/state/${selectedState}`,
    },
    enabled: !!selectedState,
  });

  // Mutation for updating election
  const updateElectionMutation = useApiMutation({
    onSuccess: () => {
      toast.success('Changes saved successfully!');
    },
    onError: (error) => {
      console.error('Error saving changes:', error);
      toast.error('Error saving changes');
    },
  });

  useEffect(() => {
    if (electionResponse?.success && electionResponse?.data?.elections?.length > 0) {
      const elections = electionResponse.data.elections;
      setElectionsData(elections);
      
      // Populate forms with all elections data
      const forms = elections.map(election => ({
        googleElectionId: election.googleElectionId,
        name: election.name,
        electionDay: election.electionDay,
        ocdDivisionId: election.ocdDivisionId,
        electionType: election.electionType,
        stateCode: election.stateCode,
        electionHours: election.electionHours || '',
        registrationDeadline: election.registrationDeadline || '',
        mailRequestDeadline: election.mailRequestDeadline || '',
        mailReturnDeadline: election.mailReturnDeadline || '',
        earlyVotingStartDate: election.earlyVotingStartDate || '',
        earlyVotingEndDate: election.earlyVotingEndDate || '',
        voterIdRequirement: election.voterIdRequirement || '',
        felonyVotingRules: election.felonyVotingRules || '',
        electionInfoUrl: election.electionInfoUrl,
        electionRegistrationUrl: election.electionRegistrationUrl,
        electionRegistrationConfirmationUrl: election.electionRegistrationConfirmationUrl || '',
        absenteeVotingInfoUrl: election.absenteeVotingInfoUrl || '',
        votingLocationFinderUrl: election.votingLocationFinderUrl || '',
        ballotInfoUrl: election.ballotInfoUrl || '',
        electionRulesUrl: election.electionRulesUrl || '',
        officialStateWebsite: election.officialStateWebsite,
        voterInfoEnriched: election.voterInfoEnriched,
        electionResultsUrl: ''
      }));
      setFormsData(forms);
      
      // Set initial collapse state: collapse all if more than one election, expand all if only one
      if (elections.length > 1) {
        setExpandedForms(new Set()); // All collapsed
      } else {
        setExpandedForms(new Set([0])); // First (and only) form expanded
      }
    }
  }, [electionResponse]);

  const handleStateChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const stateCode = e.target.value;
    setSelectedState(stateCode);
    
    if (!stateCode) {
      setElectionsData([]);
      setFormsData([]);
      setExpandedForms(new Set());
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, electionIndex: number) => {
    const { name, value, type } = e.target;
    
    setFormsData(prev => {
      const updatedForms = [...prev];
      updatedForms[electionIndex] = {
        ...updatedForms[electionIndex],
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      };
      return updatedForms;
    });
  };

  const handleToggleExpand = (index: number) => {
    setExpandedForms(prev => {
      const newExpanded = new Set(prev);
      if (newExpanded.has(index)) {
        newExpanded.delete(index);
      } else {
        newExpanded.add(index);
      }
      return newExpanded;
    });
  };

  const handleSaveChanges = async () => {
    if (electionsData.length === 0) return;
    
    try {
      const updatePromises = electionsData.map((election, index) => {
        return updateElectionMutation.mutateAsync({
          method: 'PATCH',
          url: `/admin/elections/${election.id}`,
          data: formsData[index],
        });
      });
      
      await Promise.all(updatePromises);
      toast.success('All changes saved successfully!');
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error('Error saving changes');
    }
  };

  const isFormDisabled = !selectedState || isLoading || updateElectionMutation.isPending;

  return (
    <div className="max-w-5xl w-full mx-auto">
      <h2 className="text-2xl font-bold mb-6">Update Election</h2>
      
      <div className="mb-6">
        <label htmlFor="state" className="block text-sm font-medium text-gray-700 mb-2">
          Select State
        </label>
        <select
          id="state"
          value={selectedState}
          onChange={handleStateChange}
          className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <option value="">Choose a state...</option>
          {US_STATES.map(state => (
            <option key={state.code} value={state.code}>
              {state.name}
            </option>
          ))}
        </select>
      </div>

      {!selectedState && (
        <div className="text-center text-gray-600 py-8 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-lg">Please select a state to proceed</p>
        </div>
      )}

      {/* Election Forms */}
      {formsData.length > 0 && (
        <div className="space-y-6">
          <h3 className="text-lg font-medium text-gray-900">
            Found {formsData.length} election{formsData.length > 1 ? 's' : ''} for {selectedState}
          </h3>
          
          {formsData.map((formData, index) => (
            <ElectionForm
              key={electionsData[index]?.id || index}
              formData={formData}
              electionIndex={index}
              isFormDisabled={isFormDisabled}
              handleInputChange={handleInputChange}
              isExpanded={expandedForms.has(index)}
              onToggleExpand={() => handleToggleExpand(index)}
              showCollapseButton={formsData.length > 1}
            />
          ))}
          
          <div className="pt-4">
            <button
              onClick={handleSaveChanges}
              disabled={electionsData.length === 0 || updateElectionMutation.isPending}
              className="px-6 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {updateElectionMutation.isPending ? 'Saving...' : 'Save All Changes'}
            </button>
          </div>
        </div>
      )}
      
      {formsData.length === 0 && selectedState && !isLoading && (
        <div className="text-center text-gray-600 py-8">
          No elections found for {selectedState}
        </div>
      )}

      {isLoading && (
        <div className="mt-4 text-center text-gray-600">
          Loading election data...
        </div>
      )}
    </div>
  );
}
